import { FunctionComponent, useState } from 'react'
import { GridLayout, MultiSelectSearch, SearchInput, SelectSearch } from '../../../shared'
import { SearchSchema, deserializeFromUrlSearchParams } from '../../../../misc/url.utilities'
import { useTimeoutEffect, useTagSearch } from '../../../../hooks/utils'
import { TagType } from '../../../../model/Tag'
import { QuestionnaireListPageFilters } from '../../../../pages/ManagerPage/questionnaires/QuestionnaireListPage/QuestionnaireListPage.model'
import { timeBeforeStartingResearch } from '../../../../constants'
import { VISIBILITY_FILTER_SELECT_VALUES } from '../../../../misc/filters.utils'
import {
  useVisibilityOptions,
  VisibilityLabel,
} from '../../../../hooks/utils/visibilityFilters/useVisibilityOptions.hook'

interface QuestionnairesListActionsProps {
  onFiltersChange: (filters: QuestionnaireListPageFilters) => void
  tags: ReadonlyArray<TagType>
}

function getFiltersSchema(): SearchSchema {
  return {
    search: {
      type: 'string',
      defaultValue: '',
    },
    private: {
      type: 'custom',
      customName: 'type',
      defaultValue: VISIBILITY_FILTER_SELECT_VALUES[0],
      customDeserializer: (filterParam) => {
        switch (filterParam) {
          case 'true':
            return VISIBILITY_FILTER_SELECT_VALUES[2]
          case 'false':
            return VISIBILITY_FILTER_SELECT_VALUES[1]
          default:
            return VISIBILITY_FILTER_SELECT_VALUES[0]
        }
      },
    },
    tagIds: {
      type: 'arrayOfStrings',
      customName: 'tagIds',
    },
  }
}

export const QuestionnairesListActions: FunctionComponent<QuestionnairesListActionsProps> = ({
  onFiltersChange,
  tags,
}) => {
  const urlFilters: QuestionnaireListPageFilters = deserializeFromUrlSearchParams(
    document.location.search,
    getFiltersSchema(),
  )
  const [searchFilter, setSearchFilter] = useState(urlFilters.search)
  const [tagIds, setTagIds] = useState<ReadonlyArray<string>>(urlFilters.tagIds || [])
  const [tagOptions, tagValues] = useTagSearch(tagIds, tags)

  const questionnairesVisibilityOptions = useVisibilityOptions(VisibilityLabel.Questionnaires)
  const urlSearch = new URLSearchParams(document.location.search)
  const userIdsFilter = urlSearch.get('userIds')
  const [questionnaireTypeFilter, setQuestionnaireTypeFilter] = useState(() => {
    if (userIdsFilter) {
      return (
        questionnairesVisibilityOptions.find(({ value }) => String(value) === userIdsFilter) ??
        VISIBILITY_FILTER_SELECT_VALUES[0]
      )
    }
    if (urlSearch.get('all') === 'true') return VISIBILITY_FILTER_SELECT_VALUES[0]
    if (urlSearch.get('private') === 'false') return VISIBILITY_FILTER_SELECT_VALUES[1]
    if (urlSearch.get('private') === 'true') return VISIBILITY_FILTER_SELECT_VALUES[2]
    return VISIBILITY_FILTER_SELECT_VALUES[0]
  })

  useTimeoutEffect(
    () => {
      onFiltersChange({
        search: searchFilter,
        type: questionnaireTypeFilter,
        tagIds,
      })
    },
    timeBeforeStartingResearch,
    [searchFilter, questionnaireTypeFilter, tagIds],
  )

  return (
    <GridLayout columnsTemplate="2fr 1fr 1fr" gap="medium" rowsOnMobile={2}>
      <SearchInput
        autofocus
        placeholder="Rechercher un questionnaire"
        value={searchFilter}
        onChange={({ target: { value } }) => setSearchFilter(value)}
        testId="input-questionnaire-by_name-search"
      />
      <MultiSelectSearch
        placeholder="Rechercher par label"
        icon="tag"
        testId="multi_select_search-questionnaire-by_tag-search"
        value={tagValues}
        options={tagOptions}
        onSelect={(selected) => {
          setTagIds((selected || []).map(({ value }) => value))
        }}
      />
      <SelectSearch
        placeholder="Type"
        icon="eye"
        testId="select_search-questionnaire-by_visibility-search"
        value={questionnaireTypeFilter}
        options={questionnairesVisibilityOptions}
        onSelect={(selected) => {
          if (selected !== undefined) {
            setQuestionnaireTypeFilter(selected)
          }
        }}
        clearable={false}
      />
    </GridLayout>
  )
}
